import { useEffect, useRef, useState } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'

import { App } from './App'
import { SignIn } from './SignIn'
import { Consent } from './features/oauth/Consent'
import { TikTokHubApi } from './api/client'
import { SessionExpired } from './auth/SessionExpired'
import { msUntilExpiry, sessionVerdict, shouldRenew } from './auth/expiry'
import { STORE_KEY } from './auth/session'
import { Auth } from './auth/session'
import { I18nProvider } from './i18n'
import { AppProvider } from './lib/context'
import { Toasts } from './lib/toast'
import './ui/theme.css'

// Production: the API is on its own origin (https://tik-video.mornhouse.com), baked in at build time by
// the Dockerfile's `VITE_API_BASE` — Vite inlines it, there is no runtime config. Empty = same origin,
// which is what development uses (Vite proxies /v1 to a local service, see vite.config).
const BASE_URL = import.meta.env.VITE_API_BASE ?? ''

// Apply the remembered theme and language BEFORE React renders: doing it in an effect meant the page
// flashed dark on its way to light, and a saved preference should never be visible as a flicker.
document.documentElement.dataset.theme = localStorage.getItem('tthub_theme') === 'light' ? 'light' : 'dark'
document.documentElement.lang = localStorage.getItem('tthub_lang') ?? 'en'

const auth = new Auth()
const api = new TikTokHubApi(BASE_URL, auth)

function Root() {
  const [ready, setReady] = useState<boolean>(() => auth.boot())
  const [epoch, setEpoch] = useState(0)
  const [refused, setRefused] = useState(false)

  // Two ways to learn the session is over, and both are needed. A 401 is the server's opinion and beats
  // our clock, but it arrives on the person's *next* click — possibly minutes later, mid-gesture on a
  // phone that already stopped responding. Our own clock says it first.
  useEffect(() => {
    api.onUnauthorized = () => setRefused(true)
    return () => {
      api.onUnauthorized = undefined
    }
  }, [])

  // Activity is tracked in a ref, not state: it changes on every pointer move and re-rendering the
  // whole workspace for that would be its own performance bug.
  const lastActivity = useRef(Date.now())
  useEffect(() => {
    const seen = () => {
      lastActivity.current = Date.now()
    }
    const events = ['pointerdown', 'keydown', 'wheel', 'touchstart'] as const
    events.forEach((e) => window.addEventListener(e, seen, { passive: true }))
    return () => events.forEach((e) => window.removeEventListener(e, seen))
  }, [])

  const expiresAt = auth.expiresAt
  const [renewedAt, setRenewedAt] = useState(0)

  // **One session for every tab** (owner, 2026-10-06). Another tab renewed → adopt its token quietly;
  // signed in → leave the sign-in screen; signed out or switched person → follow it.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== STORE_KEY && e.key !== null) return
      const before = auth.user?.email ?? null
      const wasReady = auth.ready
      if (!auth.boot()) {
        setRefused(false)
        setReady(false)
        return
      }
      setRefused(false)
      setRenewedAt(Date.now())
      if (!wasReady || (auth.user?.email ?? null) !== before) {
        setReady(true)
        setEpoch((n) => n + 1)
      }
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [])
  const facts = { expiresAt, source: auth.source, refused, lastActivityAt: lastActivity.current, issuedAt: auth.issuedAt }

  // Keep a working person signed in. Checked on a timer rather than hooked into every request: the
  // decision depends on the clock and on activity, not on whether something happened to be fetched.
  useEffect(() => {
    if (!ready) return
    const tick = async () => {
      if (!shouldRenew({ ...facts, lastActivityAt: lastActivity.current }, Date.now())) return
      try {
        auth.renew(await api.refreshSession())
        setRenewedAt(Date.now())
      } catch {
        // The session is either over or too old, and the 401 handler has already raised the one dialog
        // that explains it. Retrying would only make noise.
      }
    }
    const id = window.setInterval(() => void tick(), 20_000)
    void tick()
    return () => window.clearInterval(id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, epoch, expiresAt, renewedAt])

  //: Once per sign-in, ask the service who this is now: the stored user came with the login and missed
  //: directions an admin gave later (2026-10-14: a tester with AI Games was told on Videos they had none).
  const freshFor = useRef<string | null>(null)
  useEffect(() => {
    const email = auth.user?.email ?? null
    if (!ready || !email || freshFor.current === email) return
    freshFor.current = email
    void api.me().then((res) => {
      if (res.user && res.user.email === auth.user?.email
          && JSON.stringify(res.user) !== JSON.stringify(auth.user)) {
        auth.updateUser({ ...auth.user, ...res.user })
        setEpoch((n) => n + 1)
      }
    }).catch(() => { /* the 401 handler speaks for a dead session */ })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, epoch])

  useEffect(() => {
    if (!ready) return
    const wait = msUntilExpiry(facts, Date.now())
    if (wait === null) return
    const id = window.setTimeout(() => setRefused(true), wait)
    return () => window.clearTimeout(id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, epoch, expiresAt, renewedAt])

  const verdict = ready ? sessionVerdict(facts, Date.now()) : { state: 'ok' as const }

  //: **Connecting a Claude** (OAuth consent, `features/oauth/Consent.tsx`). Claude opens this in a tab
  //: of its own; the session is shared across tabs (`localStorage`), so a signed-in person lands on the
  //: consent card at once, and anybody else sees the usual sign-in and continues here — `req` stays in
  //: the URL, nothing is navigated.
  const consentReq = window.location.pathname.startsWith('/oauth/consent')
    ? new URLSearchParams(window.location.search).get('req') : null

  const signOut = () => {
    auth.clear()
    setRefused(false)
    setReady(false)
  }

  if (!ready) {
    return (
      <I18nProvider>
        <SignIn
          api={api}
          onSignedIn={(res) => {
            auth.accept(res)
            setRefused(false)
            setReady(true)
            setEpoch((n) => n + 1)
          }}
          onToken={(token) => {
            auth.setManualToken(token)
            setReady(true)
            setEpoch((n) => n + 1)
          }}
        />
      </I18nProvider>
    )
  }

  if (consentReq) {
    return (
      <I18nProvider>
        {verdict.state === 'expired' ? <SessionExpired onSignIn={signOut} /> : <Consent api={api} requestId={consentReq} />}
      </I18nProvider>
    )
  }

  return (
    <AppProvider value={{ api, auth, authEpoch: epoch, signOut }}>
      <I18nProvider>
        {/* **An ended session closes the workspace, not just covers it** (owner, 2026-10-06: "якщо юзера
            видалити сесія теж має померти одразу"). Left mounted under the dialog, a phone's video,
            input socket and polls kept running for a person the server had already shut out. */}
        {verdict.state !== 'expired' && (
          <BrowserRouter>
            <App />
          </BrowserRouter>
        )}
        <Toasts />
        {verdict.state === 'expired' && <SessionExpired onSignIn={signOut} />}
      </I18nProvider>
    </AppProvider>
  )
}

// No StrictMode on purpose. It double-invokes effects in development, and our session effect claims
// a *physical phone* on the server: mounting twice means start → stop → start, which makes the hub
// restart scrcpy and can hand us a 409 against our own just-released claim. Session cleanup is
// exercised for real by switching devices.
createRoot(document.getElementById('root')!).render(<Root />)
